import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Stack, Button, Spinner, Container, Image } from "react-bootstrap";
import {
  fetchBeds,
  fetchEthnicities,
  fetchGenders,
  fetchJobs,
  fetchPrograms,
  fetchRaces,
  fetchRelationships,
  fetchRooms,
  patchBed,
  patchFieldValue,
  patchGuardian,
  patchMember,
  postFieldValue,
} from "../../services/httpClient";
import DatePicker from "../ui/DatePicker";
import Input from "../ui/Input";
import SelectObject from "../ui/SelectObject";
import InputPhone from "../ui/InputPhone";
import { useParams } from "react-router-dom";
import ToastMsg from "../ui/ToastMsg";
import FormCheck from "../ui/FormCheckbox";
import useApp from "../../utils/useApp";
import SelectObjectTypeahead from "../ui/SelectObjectTypeahead";
import ConfirmBan from "../ui/ConfirmBan";
import ConfirmDelete from "./ConfirmDelete";
import CustomField from "../ui/CustomField";
import { UserFields } from "../../types/userFields";
import { validateUserFields } from "../../helpers/validation";

interface EditMemberState {
  user: any;
  userType: string;
  setUserToSave?: any;
  caseStatus: string;
  onClickDone: () => void;
  guardians: any[];
  household: any[];
  children: any[];
  getHousehold: () => void;
  getCase: () => void;
}

const EditMember = ({
  user,
  userType,
  setUserToSave,
  caseStatus,
  onClickDone,
  guardians,
  household,
  children,
  getHousehold,
  getCase,
}: EditMemberState) => {
  const { id } = useParams();
  const { auth }: any = useApp();
  const [isPrimaryUpdated, setIsPrimaryUpdated] = useState<boolean>(false);
  const [custom, setCustom] = useState<any[]>([]);
  const [profileImage, setProfileImage] = useState<string>("");
  const [saveIsEnabled, setSaveIsEnabled] = useState<boolean>(false);
  const [userUpdated, setUserUpdated] = useState<boolean>(false);
  const [customUpdated, setCustomUpdated] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState("");
  const [relationships, setRelationships] = useState<any[]>([]);
  const [allRelationships, setAllRelationships] = useState<any[]>([]);
  const [genders, setGenders] = useState<any[]>([]);
  const [races, setRaces] = useState<any[]>([]);
  const [ethnicities, setEthnicities] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [beds, setBeds] = useState<any[]>([]);
  const [filteredBeds, setFilteredBeds] = useState<any[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBanning, setIsBanning] = useState(false);
  const [userToBan, setUserToBan] = useState<string>();
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastText, setToastText] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [userFields, setUserFields] = useState<UserFields>({
    firstName: "",
    lastName: "",
    preferredName: "",
    email: "",
    phone: null,
    intakeDate: undefined,
    exitDate: undefined,
    birthDate: undefined,
    relationship: "",
    gender: "",
    ethnicity: "",
    race: "",
    school: "",
    grade: "",
    program: null,
    level: null,
    previousJob: "",
    job: null,
    room: null,
    previousBed: null,
    bed: null,
    isPrimary: false,
    appAccess: false,
    appAccessLast: undefined,
    isBanned: false,
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    getRelationships();
    getGenders();
    getRaces();
    getEthnicities();
    getPrograms();
    getJobs();
    if (auth?.profile.includeHMIS === 1) {
      getBeds();
    }
  }, [user]);

  const getRelationships = () => {
    fetchRelationships().then((res: any) => {
      setAllRelationships(res[0].data);
      setRelationships(
        res[0].data.filter((c: any) => {
          return c.key !== "child";
        }),
      );
    });
  };

  const getGenders = () => {
    fetchGenders().then((res: any) => {
      setGenders(res[0].data);
    });
  };

  const getRaces = () => {
    fetchRaces().then((res: any) => {
      setRaces(res[0].data);
    });
  };

  const getEthnicities = () => {
    fetchEthnicities().then((res: any) => {
      setEthnicities(res[0].data);
    });
  };

  const getPrograms = () => {
    fetchPrograms()
      .then((res: any) => {
        let programs = res[0].data;
        programs = [{ id: 0, key: 0, name: "Select from List", programLevels: [] }, ...programs];
        setPrograms(programs);
        setLevels([]);

        const selectedProgram = programs.find((p: any) => p.key === user?.program);
        if (selectedProgram) {
          if (selectedProgram.programLevels && selectedProgram.programLevels.length > 0) {
            setLevels(selectedProgram.programLevels);
            if (user?.level_id) {
              const selectedLevel = selectedProgram.programLevels.find((lvl: any) => lvl.id === user.level_id);
              if (selectedLevel) {
                setUserFields((prev) => ({ ...prev, level: selectedLevel }));
              } else {
                setUserFields((prev) => ({ ...prev, level: null }));
              }
            }
          }
          setUserFields((prev) => ({ ...prev, program: selectedProgram }));
        }
        setSaveIsEnabled(false);
        setUserToSave(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getJobs = () => {
    fetchJobs().then((res: any) => {
      const jobs = res[0].data;
      var availJobs = jobs.filter((j: any) => j.positions_filled < j.positions_total);

      //Ensure user's job is included
      const userJob = jobs.find((j: any) => j.key === user?.job);

      if (userJob && !availJobs.some((j: any) => j.id === userJob.id)) {
        availJobs.push(userJob);
      }

      availJobs = [{ id: 0, key: 0, name: "Select from List" }, ...availJobs];
      setJobs(availJobs);

      const selectedJob = availJobs.find((j: any) => j.key === user?.job);
      if (selectedJob) {
        setUserFields((prev) => ({ ...prev, job: selectedJob }));
      } else if (user?.job) {
        const currentJob = jobs.find((j: any) => j.key === user?.job);
        setUserFields((prev) => ({ ...prev, job: currentJob }));
        setJobs((prevJobs) => [currentJob, ...prevJobs]);
      } else {
        // setUserFields((prev) => ({ ...prev, job: { key: "", name: "" } }));
      }
    });
  };

  const getBeds = () => {
    fetchBeds()
      .then((res: any) => {
        var bedList = res[0].data;
        //include bed user is currently assigned to
        var currentBed;
        if (user?.bed) {
          currentBed = bedList.find((b: any) => b.key === user?.bed);
        }
        //filter to vacant beds
        bedList = bedList.filter((b: any) => b.occupant_id === null);

        if (currentBed) {
          //if user is assigned to a bed, add it to the list
          bedList.push(currentBed);
        }

        bedList.sort((a: any, b: any) => {
          // group by room name
          if (a.room_name !== b.room_name) {
            return a.room_name.localeCompare(b.room_name, undefined, { numeric: true, sensitivity: "base" });
          }
          // sort beds in each room alphanumerically
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });

        setBeds(bedList);
        if (user?.room) {
          const filtered = bedList.filter((b: any) => b.room_key === user?.room);
          setFilteredBeds(filtered);
        } else {
          setFilteredBeds(bedList);
        }
        getRooms();
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setErrMsg("");
      });
  };

  const getRooms = () => {
    fetchRooms()
      .then((res: any) => {
        const roomList = res[0].data;
        roomList.sort((a: any, b: any) => {
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });
        setRooms(roomList);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  useEffect(() => {
    if (!user) return;

    setUserFields({
      firstName: user.firstname ?? "",
      lastName: user.lastname ?? "",
      preferredName: user.preferredname ?? "",
      email: user.email ?? "",
      phone: user.phonenumber === "" ? null : user.phonenumber,
      intakeDate: user.intake_at,
      exitDate: user.exit_at,
      birthDate: user.birthdate,
      relationship: user.relationship ?? "",
      gender: user.gender ?? "",
      ethnicity: user.ethnicity ?? "",
      race: user.race ?? "",
      school: user.school ?? "",
      grade: user.grade ?? "",
      previousJob: user.job ?? "",
      ...(auth?.profile.includeHMIS === 1 && {
        previousBed: user.bed,
        room: user.room ?? "",
        bed: user.bed ?? null,
      }),
      isPrimary: user.is_primary === 1,
      appAccess: user?.appaccess === 1 ? true : false,
      appAccessLast: user.appaccess_last_at,
      isBanned: user.is_banned === 1,
    });

    if (user.profile.length > 0) {
      user?.profile?.map((p: any) => {
        const base64String = "data:image/jpeg;base64, " + p?.data;
        setProfileImage(base64String);
      });
    } else {
      setProfileImage("");
    }

    console.log("user custom fields", user.customfields);
    if (user.customfields) {
      setCustom(user.customfields);
    }
  }, [user, auth]);

  function validateUser(
    first: string,
    last: string,
    emailVal: any,
    phoneVal: any,
    program: any,
    level: any,
    appAccess = false,
    fieldType: string = "user",
  ) {
    let errors: string[] = [];
    if (levels?.length > 0) {
      errors = validateUserFields(first, last, emailVal, phoneVal, program, level, appAccess);
    } else {
      errors = validateUserFields(first, last, emailVal, phoneVal, program, null, appAccess);
    }

    if (setUserToSave) setUserToSave(true);
    if (fieldType === "custom") {
      setCustomUpdated(true);
    } else {
      setUserUpdated(true);
    }

    return {
      isValid: errors.length === 0,
      errors: errors,
    };
  }

  const updateField = (field: keyof typeof userFields, value: UserFields[keyof UserFields]) => {
    setUserFields((prev) => {
      const updated = { ...prev, [field]: value };
      const validation = validateUser(
        updated.firstName,
        updated.lastName,
        updated.email,
        updated.phone,
        updated.program,
        updated.level,
        updated.appAccess,
      );
      setSaveIsEnabled(validation.isValid);
      if (setUserToSave) setUserToSave(true);
      return updated;
    });
  };

  const updateProgram = (e: any) => {
    updateField("level", null);
    setLevels([]);
    if (e.programLevels && e.programLevels.length > 0) {
      setLevels(e.programLevels);
    }
    updateField("program", e);
  };

  const updateLevel = (e: any) => {
    if (e.key !== userFields.level?.key) {
      updateField("level", e);
    }
  };

  const updateJob = (e: any) => {
    if (e.key !== userFields.job?.key) {
      updateField("previousJob", userFields.job?.key);
      updateField("job", e);
    }
  };

  const updateRoom = (e: any) => {
    updateField("room", e.target.value);
    updateField("bed", null);
    const filtered = beds.filter((b: any) => b.room_key === e.target.value);
    setFilteredBeds(filtered);
  };

  const updateBed = (e: any) => {
    updateField("previousBed", userFields.bed);
    updateField("bed", e.target.value);
  };

  const updateIsPrimary = () => {
    if (!userFields.isPrimary) {
      setIsPrimaryUpdated(true);
    }
    updateField("isPrimary", !userFields.isPrimary);
  };

  const onClickSave = () => {
    const validation = validateUser(
      userFields.firstName,
      userFields.lastName,
      userFields.email,
      userFields.phone,
      userFields.program,
      userFields.level,
      userFields.appAccess,
    );
    setErrMsg("");

    if (validation.isValid) {
      if (userType === "Guardian") {
        if (isPrimaryUpdated) {
          const otherGuardians = guardians.filter((g: any) => {
            return g.id !== user?.id;
          });

          const otherUpdatedGuardian = {
            is_primary: false,
          };

          otherGuardians.map((o: any) => {
            patchGuardian(otherUpdatedGuardian, id, o?.id)
              .then((res: any) => {})
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg);
              });
          });
        }

        if (userUpdated) {
          var updatedUser: any = {
            firstname: userFields.firstName,
            lastname: userFields.lastName,
            preferredname: userFields.preferredName,
            is_primary: userFields.isPrimary,
            email: userFields.email,
            phonenumber: userFields.phone === "1" ? null : userFields.phone,
            relationship: userFields.relationship,
            intake_at: userFields.intakeDate,
            exit_at: userFields.exitDate,
            birthdate: userFields.birthDate,
            gender: userFields.gender,
            ethnicity: userFields.ethnicity,
            race: userFields.race,
            program: userFields.program?.key === 0 ? 0 : userFields.program?.key ? userFields.program.key : null,
            level_id: userFields.level?.id === 0 ? 0 : userFields.level?.id ? userFields.level.id : null,
            previous_job: userFields.previousJob,
            job: userFields.job?.key === 0 ? 0 : userFields.job?.key ? userFields.job.key : null,
          };

          if (updatedUser.program === 0) {
            updatedUser.level_id = null;
          }

          const prevAppAccess = user?.appaccess === 1 ? true : false;
          if (prevAppAccess !== userFields.appAccess) {
            updatedUser.appaccess = userFields.appAccess;
          }

          //update user
          patchGuardian(updatedUser, id, user?.id)
            .then((res: any) => {})
            .catch((err: any) => {
              setErrMsg(err?.response?.data?.msg);
            });

          if (auth?.profile.includeHMIS === 1) {
            if (userFields.bed != user?.bed) {
              //setting bed vacant
              if (userFields.bed == 0) {
                const updatedBed = {
                  occupant_id: userFields.bed,
                };

                patchBed(updatedBed, userFields.previousBed)
                  .then((res: any) => {})
                  .catch((err: any) => {
                    setErrMsg(err?.response?.data?.msg);
                  });
              } else if (userFields.bed) {
                const updatedBed = {
                  occupant_id: user?.id,
                  occupant_type: "guardian",
                };

                //update bed assignment
                patchBed(updatedBed, userFields.bed)
                  .then((res: any) => {})
                  .catch((err: any) => {
                    setErrMsg(err?.response?.data?.msg);
                  });
              }
            }
          }
        }

        //update / create custom field values
        if (customUpdated) {
          custom.map((cust: any) => {
            if (cust?.field_value_id) {
              //patch
              const fieldVal = {
                value: cust?.value,
              };

              patchFieldValue(fieldVal, cust?.field_value_id).catch((err: any) => {
                console.log(err);
                setErrMsg(err?.response?.data?.msg);
              });
            } else if (cust?.value) {
              //post
              const fieldVal = {
                field_id: cust?.field_id,
                person_id: user?.id,
                person_type: "guardian",
                value: cust?.value,
              };

              postFieldValue(fieldVal).catch((err: any) => {
                console.log(err);
                setErrMsg(err?.response?.data?.msg);
              });
            }
          });
        }
      } else {
        const updatedMember = {
          firstname: userFields.firstName,
          lastname: userFields.lastName,
          preferredname: userFields.preferredName,
          phonenumber: userFields.phone,
          relationship: userFields.relationship,
          intake_at: userFields.intakeDate,
          exit_at: userFields.exitDate,
          birthdate: userFields.birthDate,
          gender: userFields.gender,
          ethnicity: userFields.ethnicity,
          race: userFields.race,
          school: userFields.school,
          grade: userFields.grade,
          program: userFields.program?.key === 0 ? 0 : userFields.program?.key ? userFields.program.key : null,
          level_id: userFields.level?.id === 0 ? 0 : userFields.level?.id ? userFields.level.id : null,
          previous_job: userFields.previousJob,
          job: userFields.job?.key === 0 ? 0 : userFields.job?.key ? userFields.job.key : null,
        };

        if (updatedMember.program === 0) {
          updatedMember.level_id = null;
        }

        //update member
        patchMember(updatedMember, id, user?.id)
          .then((res: any) => {})
          .catch((err: any) => {
            setErrMsg(err?.response?.data?.msg);
          });

        if (auth?.profile.includeHMIS === 1) {
          if (userFields.bed != user?.bed) {
            //setting bed vacant
            if (userFields.bed == 0) {
              const updatedBed = {
                occupant_id: userFields.bed,
              };

              patchBed(updatedBed, userFields.previousBed)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg);
                });
            } else if (userFields.bed) {
              const updatedBed = {
                occupant_id: user?.id,
                occupant_type: "member",
              };

              //update bed assignment
              patchBed(updatedBed, userFields.bed)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg);
                });
            }
          }
        }

        //update / create custom field values
        if (customUpdated) {
          custom.map((cust: any) => {
            if (cust?.field_value_id) {
              //patch
              const fieldVal = {
                value: cust?.value,
              };

              patchFieldValue(fieldVal, cust?.field_value_id).catch((err: any) => {
                console.log(err);
                setErrMsg(err?.response?.data?.msg);
              });
            } else if (cust?.value) {
              //post
              const fieldVal = {
                field_id: cust?.field_id,
                person_id: user?.id,
                person_type: "member",
                value: cust?.value,
              };

              postFieldValue(fieldVal).catch((err: any) => {
                console.log(err);
                setErrMsg(err?.response?.data?.msg);
              });
            }
          });
        }
      }

      setUserUpdated(false);
      setCustomUpdated(false);
      setSaveIsEnabled(false);
      setUserToSave(false);
      setShowToast(true);
      setToastText(userType === "Guardian" ? "Primary Adult Updated" : "Member Updated");
      const toast = userFields.preferredName
        ? userFields.firstName + " '" + userFields.preferredName + "' " + userFields.lastName
        : userFields.firstName + " " + userFields.lastName;
      setToastBody(toast);
    } else {
      validation.errors.map((err: string) => {
        setErrMsg((prev) => (prev ? prev + "  |  " + err : err));
        return true;
      });
    }
  };

  const onClickSelectProfilePic = (event: any) => {
    fileInputRef?.current?.click();
  };

  const handleFileChange = (event: any) => {
    setErrMsg("");
    setIsImageLoading(true);

    const file = event.target.files[0];
    if (file) {
      // Check if the file is an image
      if (file.type.startsWith("image/")) {
        // The file is an image; proceed with upload
        var reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = function () {
          if (reader.result) {
            uploadFileData(reader.result.toString().split(",")[1]);
          }
        };
        reader.onerror = function (error) {
          setIsImageLoading(false);
        };
      } else {
        // Not an image; display an error or ignore the file
        setErrMsg("Please select a valid image file");
        setIsImageLoading(false);
      }
    } else {
      setIsImageLoading(false);
    }
  };

  async function uploadFileData(data: any) {
    setProfileImage(data);

    var image;
    if (user?.profile_id) {
      image = {
        profile_id: user?.profile_id,
        profile_data: data,
      };
    } else {
      image = {
        profile_data: data,
      };
    }

    if (userType === "Guardian") {
      patchGuardian(image, id, user?.id)
        .then((res: any) => {
          const base64String = "data:image/jpeg;base64, " + data;
          setProfileImage(base64String);

          setShowToast(true);
          const toast = userFields.preferredName
            ? userFields.firstName + " '" + userFields.preferredName + "' " + userFields.lastName
            : userFields.firstName + " " + userFields.lastName;
          setToastText("Guardian Profile Image Updated");
          setToastBody(toast);
          setIsImageLoading(false);
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
          setIsImageLoading(false);
        });
    } else {
      patchMember(image, id, user?.id)
        .then((res: any) => {
          const base64String = "data:image/jpeg;base64, " + data;
          setProfileImage(base64String);

          setShowToast(true);
          const toast = userFields.preferredName
            ? userFields.firstName + " '" + userFields.preferredName + "' " + userFields.lastName
            : userFields.firstName + " " + userFields.lastName;
          setToastText("Member Profile Image Updated");
          setToastBody(toast);
          setIsImageLoading(false);
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
          setIsImageLoading(false);
        });
    }
  }

  const onClickBan = () => {
    setIsBanning(true);
    setUserToBan(userFields.firstName + " " + userFields.lastName);
  };

  const onClickDelete = () => {
    setIsDeleting(true);
  };

  const onClickDeleteProfilePic = () => {
    setErrMsg("");
    const image = {
      profile_id: user?.profile_id,
      profile_data: null,
    };

    setIsLoading(true);

    if (userType === "Guardian") {
      patchGuardian(image, id, user?.id)
        .then((res: any) => {
          setProfileImage("");

          setShowToast(true);
          const toast = userFields.preferredName
            ? userFields.firstName + " '" + userFields.preferredName + "' " + userFields.lastName
            : userFields.firstName + " " + userFields.lastName;
          setToastText("Guardian Profile Image Removed");
          setToastBody(toast);
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      patchMember(image, id, user?.id)
        .then((res: any) => {
          setProfileImage("");

          setShowToast(true);
          const toast = userFields.preferredName
            ? userFields.firstName + " '" + userFields.preferredName + "' " + userFields.lastName
            : userFields.firstName + " " + userFields.lastName;
          setToastText("Member Profile Image Removed");
          setToastBody(toast);
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  return (
    <>
      <ToastMsg showToast={showToast} setShowToast={setShowToast} toastText={toastText} toastBody={toastBody} />
      {isDeleting ? (
        <ConfirmDelete
          setIsDeleting={setIsDeleting}
          user={user}
          family={[...guardians, ...children, ...household]}
          fetchHousehold={getHousehold}
          caseStatus={caseStatus}
          getCase={getCase}
          onDone={onClickDone}
          setShowToast={setShowToast}
          setToastText={setToastText}
          setToastBody={setToastBody}
        />
      ) : isBanning ? (
        <ConfirmBan
          user={user}
          previousJob={userFields.previousJob ?? ""}
          isBanned={userFields.isBanned ?? false}
          setIsBanning={setIsBanning}
          userToBan={userToBan}
          setUserToBan={setUserToBan}
          fetchHousehold={() => updateField("isBanned", !userFields.isBanned)}
          resetCurrentView={() => {}}
          type={userType}
          caseStatus={caseStatus}
          setShowToast={setShowToast}
          setToastText={setToastText}
          setToastBody={setToastBody}
        />
      ) : isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <Row className="mb-3">
            <Col>
              <h4 className="app-header-text">{"Edit Household Member"}</h4>
            </Col>
            <Col className="text-end">
              <Stack className="stack-float" direction="horizontal" gap={3}>
                <Button className="app-danger-color button" onClick={() => onClickDelete()} disabled={isDeleting}>
                  Delete
                </Button>
                <div className="vr" />
                <Button className="app-secondary-color button" onClick={() => onClickSave()} disabled={!saveIsEnabled}>
                  Save
                </Button>
                <div className="vr" />
                <Button className="app-primary-color button" onClick={() => onClickDone()}>
                  Cancel
                </Button>
              </Stack>
            </Col>
          </Row>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <Stack gap={3} className="mb-3">
            <Row className="h-auto">
              <Col xs={3}>
                <Input
                  label="First Name"
                  type="text"
                  value={userFields.firstName}
                  onUpdate={(e: any) => updateField("firstName", e.target.value)}
                  required={true}
                />
              </Col>
              <Col xs={3}>
                <Input
                  label="Last Name"
                  type="text"
                  value={userFields.lastName}
                  onUpdate={(e: any) => updateField("lastName", e.target.value)}
                  required={true}
                />
              </Col>
              <Col xs={3}>
                <Input
                  label="Preferred Name"
                  type="text"
                  value={userFields.preferredName}
                  onUpdate={(e: any) => updateField("preferredName", e.target.value)}
                />
              </Col>
            </Row>
            <Row className="h-auto">
              <Col xs={6}>
                <Stack gap={3}>
                  <Row>
                    {userType === "Guardian" && (
                      <Col xs={6}>
                        <Input
                          label="Email"
                          type="text"
                          value={userFields.email}
                          onUpdate={(e: any) => updateField("email", e.target.value)}
                          required={userFields.appAccess}
                        />
                      </Col>
                    )}
                    <Col xs={6}>
                      <InputPhone
                        label="Phone"
                        value={userFields.phone}
                        onUpdate={(e: any) => updateField("phone", e)}
                        required={userType === "Guardian" ? userFields.appAccess : false}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={6}>
                      <DatePicker
                        label="Intake Date"
                        currentValue={userFields.intakeDate}
                        setCurrentValue={(e: any) => updateField("intakeDate", e)}
                        hasMaxDate={true}
                        disabled={caseStatus === "active" ? false : true}
                      />
                    </Col>
                    {user?.exit_at && (
                      <Col xs={6}>
                        <DatePicker
                          label="Exit Date"
                          currentValue={userFields.exitDate}
                          setCurrentValue={(e: any) => updateField("exitDate", e)}
                          hasMaxDate={true}
                          disabled={true}
                        />
                      </Col>
                    )}
                    <Col className={user?.exit_at ? "col-6 mt-3" : "col-6"}>
                      <DatePicker
                        label="Birth Date"
                        currentValue={userFields.birthDate}
                        setCurrentValue={(e: any) => updateField("birthDate", e)}
                        hasMaxDate={true}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={6}>
                      <SelectObject
                        label="Relationship"
                        value={userFields.relationship}
                        options={userType !== "Guardian" ? allRelationships : relationships}
                        onUpdate={(e: any) => updateField("relationship", e.target.value)}
                      />
                    </Col>
                    <Col xs={6}>
                      <SelectObject
                        label="Gender"
                        value={userFields.gender}
                        options={genders}
                        onUpdate={(e: any) => updateField("gender", e.target.value)}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={6}>
                      <SelectObject
                        label="Ethnicity"
                        value={userFields.ethnicity}
                        options={ethnicities}
                        onUpdate={(e: any) => updateField("ethnicity", e.target.value)}
                      />
                    </Col>
                    <Col xs={6}>
                      <SelectObject
                        label="Race"
                        value={userFields.race}
                        options={races}
                        onUpdate={(e: any) => updateField("race", e.target.value)}
                      />
                    </Col>
                  </Row>
                </Stack>
              </Col>
              <Col xs={3}>
                {isImageLoading ? (
                  <Button size="sm" className="spinner-button spinner-button-main">
                    <Spinner animation="border" style={{ color: "#F6893D" }} />
                  </Button>
                ) : (
                  <Stack gap={3}>
                    {profileImage ? (
                      <Image src={profileImage} rounded className="image-profile" />
                    ) : (
                      <Image src="/blank-profile-picture.jpg" className="image-profile" />
                    )}
                    <Button className="app-primary-color-inverse button" onClick={(e: any) => onClickSelectProfilePic(e)}>
                      {profileImage ? "Change Image" : "Upload Image"}
                    </Button>
                    {profileImage && (
                      <Button className="app-danger-color button" onClick={() => onClickDeleteProfilePic()}>
                        Remove Image
                      </Button>
                    )}
                  </Stack>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept="image/*"
                  onChange={handleFileChange} // Handle the file change
                />
              </Col>
            </Row>
            {user?.relationship === "child" && (
              <Row className="h-auto">
                <Col xs={3}>
                  <Input
                    label="School"
                    type="text"
                    value={userFields.school}
                    onUpdate={(e: any) => updateField("school", e.target.value)}
                  />
                </Col>
                <Col xs={3}>
                  <Input label="Grade" type="text" value={userFields.grade} onUpdate={(e: any) => updateField("grade", e.target.value)} />
                </Col>
              </Row>
            )}
            {(programs.length > 0 || jobs.length > 0) && (
              <Row className="h-auto">
                {programs.length > 0 && (
                  <Col xs={3}>
                    <SelectObjectTypeahead
                      label="Program"
                      value={userFields.program}
                      options={programs}
                      onUpdate={(e: any) => updateProgram(e)}
                    />
                  </Col>
                )}
                {levels.length > 0 && (
                  <Col xs={3}>
                    <SelectObjectTypeahead
                      label="Program Level"
                      value={userFields.level}
                      options={levels}
                      onUpdate={(e: any) => updateLevel(e)}
                    />
                  </Col>
                )}
                {jobs.length > 0 && (
                  <Col xs={3}>
                    <SelectObjectTypeahead label="Job" value={userFields.job} options={jobs} onUpdate={(e: any) => updateJob(e)} />
                  </Col>
                )}
              </Row>
            )}
            {rooms.length > 0 && beds.length > 0 && (
              <Row className="h-auto">
                <Col xs={3}>
                  <SelectObject label="Room" value={userFields.room} options={rooms} onUpdate={(e: any) => updateRoom(e)} />
                </Col>
                <Col xs={3}>
                  <SelectObject
                    label="Bed"
                    value={userFields.bed}
                    options={filteredBeds}
                    onUpdate={(e: any) => updateBed(e)}
                    disabled={userFields.room ? false : true}
                    initialOption="Vacant"
                  />
                </Col>
              </Row>
            )}
          </Stack>
          {user?.customfields?.length > 0 && (
            <Row className="h-auto">
              {user?.customfields?.map((c: any, index: number) => (
                <CustomField
                  key={index}
                  customField={c}
                  setCustom={setCustom}
                  userId={user?.id}
                  userType={userType}
                  enableSave={() =>
                    validateUser(
                      userFields.firstName,
                      userFields.lastName,
                      userFields.email,
                      userFields.phone,
                      userFields.program,
                      userFields.level,
                      userFields.appAccess,
                      "custom",
                    )
                  }
                  setSaveIsEnabled={setSaveIsEnabled}
                  columns={3}
                />
              ))}
            </Row>
          )}
          <Row className="h-auto mb-3">
            <Col xs={6} className="text-start">
              <Stack direction="horizontal" gap={3}>
                <Button className="app-tertiary-color-inverse button" onClick={() => onClickBan()}>
                  {userFields.isBanned ? "Unban" : "Ban"}
                </Button>
                <small className="text-muted">{user?.number_infractions + " Infraction(s)"}</small>
              </Stack>
            </Col>
            {userType === "Guardian" && (
              <Col xs={6}>
                <FormCheck label="Main Contact" value={userFields.isPrimary} onUpdate={(e: any) => updateIsPrimary()} />
                <FormCheck
                  label="Mobile App Access"
                  value={userFields.appAccess}
                  onUpdate={(e: any) => updateField("appAccess", !userFields.appAccess)}
                  css={"mb-0"}
                />
                <small>{"(Requires email or phone)"}</small>
              </Col>
            )}
          </Row>
        </>
      )}
    </>
  );
};

export default EditMember;
